import { Controller } from "@hotwired/stimulus";

const PRICE_MANUAL_TYPES = [
  "SitewatchProduct",
  "NxtProduct",
  "SonnysProduct",
  "WashConnectProduct",
];

export default class extends Controller {
  static targets = [
    "replicationGroupSelect",
    "icsOptions",
    "skipIcsCustomerCreation",
    "icsCorporateSite",
    "locationsContainer",
    "showTotalCostCheckbox",
    "showTaxRatesCheckbox",
    "tosCheckbox",
    "disabledCheckbox",
    "existingMemberConfirmation",
    "blockSaleMessage",
    "validationError",
    "validationErrorTop",
    "sitewatchLocationSales",
    "drbAlert",
    "cvvRequiredCheckbox",
    "zipcodeRequiredCheckbox",
    "licensePlateRequiredCheckbox",
    "rfidRequiredCheckbox",
  ];

  static values = {
    takenNames: Array,
    useWashAssistInternalApi: Boolean,
    tosOnLocationGroup: Boolean,
    showDeliveryUrl: Boolean,
    multiQtyCheckoutFormEnabled: Boolean,
    sitewatchEwashParam: Boolean,
    ewashRecordsForProducts: Boolean,
    rgOptionsBaseUrl: String,
    dirty: { type: Boolean, default: false },
  };

  connect() {
    this._rgOptionsFrame = this.element.querySelector("#replication-group-options");
    this._boundOnOptionsLoaded = () => this._onOptionsLoaded();
    if (this._rgOptionsFrame) {
      this._rgOptionsFrame.addEventListener("turbo:frame-load", this._boundOnOptionsLoaded);

      // if the frame already loaded before this controller connected, process now
      if (this._rgOptionsFrame.querySelector("[data-product-options]")) {
        this._onOptionsLoaded();
      }
    }

    this._syncLicensePlateRequiredLock();
    this._syncRfidRequiredLock();
  }

  disconnect() {
    if (this._rgOptionsFrame && this._boundOnOptionsLoaded) {
      this._rgOptionsFrame.removeEventListener("turbo:frame-load", this._boundOnOptionsLoaded);
    }
  }

  get showTotalCost() {
    if (!this.hasShowTotalCostCheckboxTarget) return false;
    return this._checkboxChecked(this.showTotalCostCheckboxTarget);
  }

  toggleExistingMemberConfirmation() {
    const productsEl = this.element.querySelector(
      "[data-controller~='admin--checkout-form-products']"
    );
    if (!productsEl) return;

    const productsController = this.application.getControllerForElementAndIdentifier(
      productsEl,
      "admin--checkout-form-products"
    );
    if (productsController) {
      productsController._updateBlockSaleMessage();
    }
  }

  toggleShowTotalCost() {
    const checked = this._checkboxChecked(this.showTotalCostCheckboxTarget);
    const taxCheckbox = this._findCheckbox(this.showTaxRatesCheckboxTarget);

    if (taxCheckbox) {
      if (!checked) {
        taxCheckbox.checked = false;
      }
      taxCheckbox.disabled = !checked;

      const wrapper = taxCheckbox.closest(".flex.items-center");
      const label = wrapper?.querySelector(`label[for="${taxCheckbox.id}"]`);
      if (label) {
        label.classList.toggle("opacity-50", !checked);
        label.classList.toggle("cursor-not-allowed", !checked);
      }
    }

    if (!checked) {
      this._updateAllProductTaxRateVisibility(false);
    }

    this.dispatch("totalCostChanged", {
      detail: { showTotalCost: checked },
    });
    this._updateAllProductPriceVisibility();
  }

  toggleShowTaxRates() {
    const checked = this._checkboxChecked(this.showTaxRatesCheckboxTarget);
    this._updateAllProductTaxRateVisibility(checked);
  }

  replicationGroupChanged(event) {
    const rgId = event.target.value;
    if (!rgId) return;

    // clear the old group's selections first, so _syncOptionsToSelects can't restore a stale
    // foreign key against the new group's options
    this._resetProductSelections();

    this.dispatch("replicationGroupChanged", {
      detail: { replicationGroupId: rgId },
    });

    this._loadReplicationGroupOptions(rgId);
  }

  _resetProductSelections() {
    const clearSelect = (selectEl) => {
      selectEl.value = "";
      if ("selectedValue" in selectEl.dataset) selectEl.dataset.selectedValue = "";
    };

    ["foreignKeySelect", "existingMemberForeignKey", "switchProductSelect"].forEach((target) => {
      this.element
        .querySelectorAll(`[data-admin--checkout-form-products-target='${target}']`)
        .forEach(clearSelect);
    });

    // clear the hidden metadata productChanged keeps in sync with the selection
    ["typeHidden", "isMembershipHidden", "productTypeHidden"].forEach((target) => {
      this.element
        .querySelectorAll(`[data-admin--checkout-form-products-target='${target}']`)
        .forEach((el) => (el.value = ""));
    });
  }

  validateAndSubmit(event) {
    // formnovalidate opts out of our custom validation too (used by the preview-refresh submit).
    if (event.submitter?.formNoValidate) return;

    this._clearValidationErrors();
    const errors = this._validate();

    if (errors.length > 0) {
      event.preventDefault();
      this._displayValidationErrors(errors);
      return;
    }

    this.dirtyValue = false;
    this.dispatch("clean");
  }

  markDirty() {
    if (this.dirtyValue) return;
    this.dirtyValue = true;
    this.dispatch("dirty");
  }

  productForeignKeyChanged(event) {
    const foreignKey = event.target.value;
    const product = this._findProductByForeignKey(foreignKey);
    if (!product) return;

    const row = event.target.closest("[data-admin--checkout-form-products-target='productRow']");
    if (!row) return;

    const priceField = row.querySelector(
      "[data-admin--checkout-form-products-target='priceField']"
    );
    if (priceField) {
      const shouldShow = this._shouldShowPrice(product);
      priceField.classList.toggle("hidden", !shouldShow);

      // for products with an API-fixed price, populate and disable the field so the user
      // sees what they'll be charging but can't edit it (the model's set_price overrides edits)
      const priceInput = priceField.querySelector("input");
      if (priceInput && this._priceIsFixed(product)) {
        if (product.price != null) priceInput.value = product.price;
        priceInput.disabled = true;
      } else if (priceInput) {
        priceInput.disabled = false;
      }
    }

    this._updateIcsOptionsVisibility();
  }

  _priceIsFixed(product) {
    return product.type === "WashAssistPortalProduct";
  }

  getAvailableProducts() {
    return this._getProductsFromSource("[data-product-options]");
  }

  getSwitchProducts() {
    return this._getProductsFromSource("[data-switch-product-options]");
  }

  // called by turbo frame load and by products controller when adding a new row
  populateProductSelects() {
    const frame = this._rgOptionsFrame;
    if (!frame) return;

    const productOptionsHtml = frame.querySelector("[data-product-options]")?.innerHTML;
    const switchOptionsHtml = frame.querySelector("[data-switch-product-options]")?.innerHTML;

    if (productOptionsHtml) {
      this._syncOptionsToSelects(
        "[data-admin--checkout-form-products-target='foreignKeySelect']",
        productOptionsHtml
      );
      this._syncOptionsToSelects(
        "[data-admin--checkout-form-products-target='existingMemberForeignKey']",
        productOptionsHtml
      );
    }

    if (switchOptionsHtml) {
      this._syncOptionsToSelects(
        "[data-admin--checkout-form-products-target='switchProductSelect']",
        switchOptionsHtml
      );
    }
  }

  _loadReplicationGroupOptions(rgId) {
    if (!this._rgOptionsFrame || !this.rgOptionsBaseUrlValue) return;

    const url = `${this.rgOptionsBaseUrlValue}?checkout_form[replication_group_id]=${rgId}`;
    this._rgOptionsFrame.src = url;
  }

  _onOptionsLoaded() {
    const frame = this._rgOptionsFrame;
    if (!frame) return;

    const errorEl = frame.querySelector("[data-error]");
    if (errorEl) {
      this._showFetchError(errorEl.dataset.error);
      return;
    }

    const meta = frame.querySelector("[data-location-count]");
    const isDrb = meta?.dataset?.isDrb === "true";

    this.populateProductSelects();
    this._updateIcsContainerVisibility();
    this._populateLocationSelect();
    this._updateDeliveryUrlVisibility();
    this._updateSitewatchRequiredFields(isDrb);
  }

  // the model enforces this on save for DRB; this just keeps the UI in sync as the rep group changes
  _updateSitewatchRequiredFields(isDrb) {
    if (this.hasCvvRequiredCheckboxTarget) {
      this._setCheckboxLocked(this.cvvRequiredCheckboxTarget, isDrb);
    }
    if (this.hasZipcodeRequiredCheckboxTarget) {
      this._setCheckboxLocked(this.zipcodeRequiredCheckboxTarget, isDrb);
    }
  }

  _setCheckboxLocked(checkbox, locked) {
    if (locked) checkbox.checked = true;
    checkbox.disabled = locked;

    const label = this.element.querySelector(`label[for="${checkbox.id}"]`);
    if (label) {
      label.classList.toggle("opacity-50", locked);
      label.classList.toggle("cursor-not-allowed", locked);
    }
  }

  // Keep the "Require License Plate" / "Require RFID tag" boxes in sync with the identifier type:
  // an identifier the form doesn't collect can't be required (lock the box off), and an identifier
  // the form is restricted to is always required (lock the box on). Fired on change of the select.
  toggleIdentifierLocks() {
    this._syncLicensePlateRequiredLock();
    this._syncRfidRequiredLock();
  }

  _syncLicensePlateRequiredLock() {
    if (!this.hasLicensePlateRequiredCheckboxTarget) return;

    const rfidOnly = this._requiredIdentifierType() === "rfid_barcode";
    this._setIdentifierCheckboxLock(this.licensePlateRequiredCheckboxTarget, rfidOnly);
  }

  _syncRfidRequiredLock() {
    if (!this.hasRfidRequiredCheckboxTarget) return;

    // RFID-only forms always require the tag (requires_rfid_tag? is true regardless of the stored
    // value), so lock the box on. License-plate-only forms never collect RFID, so lock it off.
    const type = this._requiredIdentifierType();
    const rfidOnly = type === "rfid_barcode";
    const licensePlateOnly = type === "license_plate";
    this._setIdentifierCheckboxLock(
      this.rfidRequiredCheckboxTarget,
      rfidOnly || licensePlateOnly,
      rfidOnly
    );
  }

  _requiredIdentifierType() {
    const input = this.element.querySelector(
      "input[name='checkout_form[required_identifier_type]']"
    );
    return input?.value;
  }

  _setIdentifierCheckboxLock(checkbox, locked, lockedChecked = false) {
    if (locked) checkbox.checked = lockedChecked;
    checkbox.disabled = locked;

    const label = this.element.querySelector(`label[for="${checkbox.id}"]`);
    if (label) {
      label.classList.toggle("opacity-50", locked);
      label.classList.toggle("cursor-not-allowed", locked);
    }
  }

  _syncOptionsToSelects(selectSelector, optionsHtml) {
    const selects = this.element.querySelectorAll(selectSelector);
    selects.forEach((selectEl) => {
      const currentVal = selectEl.dataset.selectedValue || selectEl.value || "";
      const placeholder = selectEl.options[0]?.value === "" ? selectEl.options[0].text : "";

      selectEl.innerHTML = optionsHtml;

      // restore the original placeholder if it differs from the template's
      if (placeholder && selectEl.options[0]?.value === "") {
        selectEl.options[0].text = placeholder;
      }

      if (currentVal) {
        const opt = selectEl.querySelector(`option[value="${CSS.escape(currentVal)}"]`);
        if (opt) opt.selected = true;
      }
    });
  }

  _populateLocationSelect() {
    if (!this.hasLocationsContainerTarget) return;

    const frame = this._rgOptionsFrame;
    if (!frame) return;

    const meta = frame.querySelector("[data-location-count]");
    const locationCount = parseInt(meta?.dataset?.locationCount || "0", 10);
    const isDrb = meta?.dataset?.isDrb === "true";

    const wrapper = this.locationsContainerTarget.querySelector(".locations-select");
    if (wrapper) {
      wrapper.classList.toggle("hidden", locationCount <= 1);
    }

    if (this.hasSitewatchLocationSalesTarget) {
      this.sitewatchLocationSalesTarget.classList.toggle("hidden", !(isDrb && locationCount > 1));
    }

    const multiselect = this.element.querySelector(
      "[data-admin--checkout-form-editor-target='locationsContainer'] [data-controller*='multiselect']"
    );
    const msController =
      multiselect &&
      this.application.getControllerForElementAndIdentifier(multiselect, "multiselect");

    if (locationCount <= 1) {
      if (msController && typeof msController.clearSelections === "function") {
        msController.clearSelections();
      }
      return;
    }

    const locationSource = frame.querySelector("[data-location-options]");
    if (!locationSource) return;

    const locationOptions = Array.from(locationSource.querySelectorAll("[data-value]")).map(
      (el) => ({
        value: el.dataset.value,
        label: el.dataset.label,
      })
    );

    if (msController && typeof msController.setOptions === "function") {
      msController.setOptions(locationOptions, true);
    }
  }

  _getProductsFromSource(sourceSelector) {
    const frame = this._rgOptionsFrame;
    if (!frame) return [];

    const source = frame.querySelector(sourceSelector);
    if (!source) return [];

    return Array.from(source.querySelectorAll("option[value]:not([value=''])")).map((opt) => ({
      foreign_key: opt.value,
      name: opt.textContent,
      type: opt.dataset.type,
      is_membership: opt.dataset.isMembership === "true",
      product_type: opt.dataset.productType,
      show_price: opt.dataset.showPrice === "true",
      pos_name: opt.dataset.posName || "",
      image_url: opt.dataset.imageUrl || "",
      gift_card_value: opt.dataset.giftCardValue || null,
      price:
        opt.dataset.price !== undefined && opt.dataset.price !== ""
          ? parseFloat(opt.dataset.price)
          : null,
    }));
  }

  _findProductByForeignKey(foreignKey) {
    if (!foreignKey) return null;
    return this.getAvailableProducts().find(
      (p) => p.foreign_key?.toString() === foreignKey?.toString()
    );
  }

  _showFetchError(message) {
    const text = `Error fetching products: ${message}`;
    if (this.hasValidationErrorTopTarget) {
      this.validationErrorTopTarget.textContent = text;
      this.validationErrorTopTarget.classList.remove("hidden");
    }
    if (this.hasValidationErrorTarget) {
      this.validationErrorTarget.textContent = text;
      this.validationErrorTarget.classList.remove("hidden");
    }
  }

  _shouldShowPrice(product) {
    if (product.type === "WashAssistProduct" && this.useWashAssistInternalApiValue) {
      return false;
    }
    if (product.show_price) return true;
    if (this._priceIsFixed(product)) return true;
    const showTotalCost = this.showTotalCost;
    const priceManuallySet = PRICE_MANUAL_TYPES.includes(product.type);
    return showTotalCost && priceManuallySet;
  }

  _validate() {
    const errors = [];

    const nameInput = this.element.querySelector("input[name='checkout_form[name]']");
    const name = nameInput?.value?.trim();
    if (!name) {
      errors.push({ message: "Name is required", field: nameInput });
    } else if (this.takenNamesValue.includes(name)) {
      errors.push({ message: "Name is already taken", field: nameInput });
    }

    const redirectUrlInput = this.element.querySelector(
      "input[name='checkout_form[redirect_url]']"
    );
    const redirectUrl = redirectUrlInput?.value?.trim();
    if (redirectUrl) {
      const testUrl = redirectUrl.replace(/\{\{(\w+)\}\}/g, "placeholder");
      try {
        new URL(testUrl);
      } catch {
        errors.push({ message: "Redirect URL is not a valid URL", field: redirectUrlInput });
      }
    }

    const tosEnabled = this.hasTosCheckboxTarget && this._checkboxChecked(this.tosCheckboxTarget);
    if (tosEnabled) {
      const tosTextArea = this.element.querySelector(
        "textarea[name='checkout_form[terms_of_service]']"
      );
      const tosText = tosTextArea?.value?.trim();
      if (!tosText && !this.tosOnLocationGroupValue) {
        errors.push({
          message: "Terms of Service must be defined below or on Location Group",
          field: tosTextArea,
        });
      }
    }

    const checkoutLimitInput = this.element.querySelector(
      "input[name='checkout_form[customer_checkout_limit]']"
    );
    const checkoutLimitVal = checkoutLimitInput?.value?.trim();
    if (checkoutLimitVal) {
      const limitNum = Number(checkoutLimitVal);
      if (!Number.isInteger(limitNum) || limitNum < 1) {
        errors.push({
          message: "Maximum Checkouts Per Customer must be a positive whole number",
          field: checkoutLimitInput,
        });
      }
    }

    this._validateProducts(errors);
    this._validateCustomFields(errors);
    this._validateBlockSaleMessage(errors);

    return errors;
  }

  _validateProducts(errors) {
    const isDisabled =
      this.hasDisabledCheckboxTarget && this._checkboxChecked(this.disabledCheckboxTarget);

    const availableProducts = this.getAvailableProducts();
    const rows = this.element.querySelectorAll(
      "[data-admin--checkout-form-products-target='productRow']:not(.hidden)"
    );

    rows.forEach((row, idx) => {
      const num = idx + 1;
      const fkSelect = row.querySelector(
        "[data-admin--checkout-form-products-target='foreignKeySelect']"
      );
      const foreignKey = fkSelect?.value;
      const nameInput = row.querySelector("input[name*='[name]']");
      const productName = nameInput?.value?.trim();

      if (!isDisabled && foreignKey && availableProducts.length > 0) {
        const valid = availableProducts.some((p) => String(p.foreign_key) === foreignKey);
        if (!valid) {
          errors.push({
            message: `Product ${num}: Product must exist in the selected Replication Group`,
            field: fkSelect,
          });
        }
      }

      // A POS-backed form (one with products to choose from) requires a selection on every row,
      // regardless of any manually-entered price — otherwise the blank selection reaches the server
      // and fails server-side validation (e.g. WashConnectProduct#wash_connect_inventory_item_id).
      // A manual form with no available products keeps the original price-as-exemption behavior.
      const hasPrice = !!row.querySelector("input[name*='[price]']")?.value;
      if (!isDisabled && !foreignKey && (availableProducts.length > 0 || !hasPrice)) {
        errors.push({ message: `Product ${num}: Product must be selected`, field: fkSelect });
      }

      if (!productName) {
        errors.push({ message: `Product ${num}: Product name is required`, field: nameInput });
      }

      const priceField = row.querySelector(
        "[data-admin--checkout-form-products-target='priceField']"
      );
      if (priceField && !priceField.classList.contains("hidden")) {
        const priceInput = priceField.querySelector("input");
        if (!priceInput?.value) {
          errors.push({ message: `Product ${num}: Price is required`, field: priceInput });
        }
      }

      const emSection = row.querySelector(
        "[data-admin--checkout-form-products-target='existingMembersSection']"
      );
      if (emSection && !emSection.classList.contains("hidden")) {
        const emFk = row.querySelector(
          "[data-admin--checkout-form-products-target='existingMemberForeignKey']"
        )?.value;
        if (emFk) {
          const emPriceInput = row
            .querySelector("[data-admin--checkout-form-products-target='existingMemberPriceField']")
            ?.querySelector("input");
          if (priceField && !priceField.classList.contains("hidden") && !emPriceInput?.value) {
            errors.push({
              message: `Product ${num}: Price for existing members is required`,
              field: emPriceInput,
            });
          }
        }
      }

      const ssSection = row.querySelector(
        "[data-admin--checkout-form-products-target='scheduledSwitchSection']"
      );
      if (ssSection && !ssSection.classList.contains("hidden")) {
        const ssFkSelect = row.querySelector(
          "[data-admin--checkout-form-products-target='switchProductSelect']"
        );
        const ssFk = ssFkSelect?.value;
        const timingTypeSelect = row.querySelector(
          "[data-admin--checkout-form-products-target='timingTypeSelect']"
        );
        const timingType = timingTypeSelect?.value;
        const daysToWaitInput = row.querySelector("input[name*='[scheduled_switch_days_to_wait]']");
        const rechargesToWaitInput = row.querySelector(
          "input[name*='[scheduled_switch_recharges_to_wait]']"
        );
        const daysToWait = daysToWaitInput?.value;
        const rechargesToWait = rechargesToWaitInput?.value;

        if (!ssFk && (daysToWait || rechargesToWait || timingType)) {
          errors.push({
            message: `Product ${num}: Switch product must be selected`,
            field: ssFkSelect,
          });
        }
        if (ssFk) {
          const isRecharges = timingType?.startsWith("recharges");
          if (isRecharges && !rechargesToWait) {
            errors.push({
              message: `Product ${num}: Recharges to wait is required`,
              field: rechargesToWaitInput,
            });
          } else if (!isRecharges && !daysToWait) {
            errors.push({
              message: `Product ${num}: Days to wait is required`,
              field: daysToWaitInput,
            });
          }
        }
      }

      if (this.showDeliveryUrlValue) {
        const deliveryUrlInput = row.querySelector("input[name*='[delivery_url]']");
        if (deliveryUrlInput?.value && !deliveryUrlInput.value.includes("%{barcode}")) {
          errors.push({
            message: `Product ${num}: Delivery URL must contain %{barcode}`,
            field: deliveryUrlInput,
          });
        }
      }

      const voucherSection = row.querySelector(
        "[data-admin--checkout-form-products-target='voucherSection']"
      );
      if (voucherSection && !voucherSection.classList.contains("hidden")) {
        const voucherTypeSelect = row.querySelector("select[name*='[voucher_type_id]']");
        const voucherQtyInput = row.querySelector("input[name*='[voucher_quantity]']");
        const voucherTypeId = voucherTypeSelect?.value;
        const voucherQty = voucherQtyInput?.value;
        if (voucherTypeId && !voucherQty) {
          errors.push({
            message: `Product ${num}: Voucher quantity must be specified`,
            field: voucherQtyInput,
          });
        }
        if (!voucherTypeId && voucherQty) {
          errors.push({
            message: `Product ${num}: Voucher type must be specified if quantity is set`,
            field: voucherTypeSelect,
          });
        }
      }
    });
  }

  _validateCustomFields(errors) {
    const rows = this.element.querySelectorAll(
      "[data-admin--checkout-form-custom-fields-target='fieldRow']:not(.hidden)"
    );

    rows.forEach((row, idx) => {
      const num = idx + 1;
      const codeInput = row.querySelector("input[name*='[code]']");
      const labelInput = row.querySelector("input[name*='[label]']");
      const fieldTypeSelect = row.querySelector("select[name*='[field_type]']");

      if (!codeInput?.value?.trim()) {
        errors.push({ message: `Custom field ${num}: Code is required`, field: codeInput });
      }
      if (!labelInput?.value?.trim()) {
        errors.push({ message: `Custom field ${num}: Label is required`, field: labelInput });
      }

      if (fieldTypeSelect?.value === "radio") {
        const optionsList = row.querySelector(
          "[data-admin--checkout-form-custom-fields-target='optionsList']"
        );
        const optionRows = optionsList?.querySelectorAll(
          "[data-admin--checkout-form-custom-fields-target='optionRow']"
        );
        if (!optionRows || optionRows.length === 0) {
          errors.push({
            message: `Custom field ${num}: Radio field must have at least one option`,
            field: fieldTypeSelect,
          });
        }
      }
    });
  }

  _validateBlockSaleMessage(errors) {
    const blockSaleMsg = this.element.querySelector(
      "textarea[name='checkout_form[block_sale_message]']"
    )?.value;
    if (!blockSaleMsg?.includes("%{barcode}")) return;

    const rows = this.element.querySelectorAll(
      "[data-admin--checkout-form-products-target='productRow']:not(.hidden)"
    );
    const missingPrice = Array.from(rows).some((row) => {
      const emSection = row.querySelector(
        "[data-admin--checkout-form-products-target='existingMembersSection']"
      );
      if (!emSection || emSection.classList.contains("hidden")) return false;

      const emFk = row.querySelector(
        "[data-admin--checkout-form-products-target='existingMemberForeignKey']"
      )?.value;
      if (!emFk) return false;

      const emPrice = row
        .querySelector("[data-admin--checkout-form-products-target='existingMemberPriceField']")
        ?.querySelector("input")?.value;
      return !emPrice;
    });

    if (missingPrice) {
      errors.push(
        "When using '%{barcode}' in block sale message, all products must have a price specified for existing members"
      );
    }
  }

  _clearValidationErrors() {
    this.element.querySelectorAll("[data-validation-error]").forEach((el) => {
      el.classList.remove("border-red-500");
      el.removeAttribute("data-validation-error");
    });
    this.element.querySelectorAll("[data-validation-error-msg]").forEach((el) => el.remove());

    if (this.hasValidationErrorTarget) {
      this.validationErrorTarget.classList.add("hidden");
      this.validationErrorTarget.replaceChildren();
    }
    if (this.hasValidationErrorTopTarget) {
      this.validationErrorTopTarget.classList.add("hidden");
      this.validationErrorTopTarget.replaceChildren();
    }
  }

  _displayValidationErrors(errors) {
    const errorMessages = errors.map((e) => (typeof e === "string" ? e : e.message));

    const buildAlert = () => {
      const alert = document.createElement("div");
      alert.setAttribute("role", "alert");
      alert.className =
        "relative w-full rounded-lg border px-4 py-2 text-destructive border-destructive/50 bg-red-50";

      const flexWrap = document.createElement("div");
      flexWrap.className = "flex gap-2";

      const iconWrap = document.createElement("div");
      iconWrap.className = "flex-shrink-0 mt-0.5";
      iconWrap.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-destructive"><circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/></svg>`;

      const content = document.createElement("div");
      content.className = "flex-1 min-w-0";

      const title = document.createElement("h5");
      title.className = "text-sm font-medium text-black";
      title.textContent =
        errorMessages.length === 1
          ? "1 error prevented this form from being saved"
          : `${errorMessages.length} errors prevented this form from being saved`;
      content.appendChild(title);

      const list = document.createElement("ul");
      list.className = "mt-1 text-sm text-muted-foreground list-disc pl-4";
      errorMessages.forEach((msg) => {
        const li = document.createElement("li");
        li.textContent = msg;
        list.appendChild(li);
      });
      content.appendChild(list);

      flexWrap.appendChild(iconWrap);
      flexWrap.appendChild(content);
      alert.appendChild(flexWrap);
      return alert;
    };

    const renderErrors = (target) => {
      target.replaceChildren(buildAlert());
      target.classList.remove("hidden");
    };

    if (this.hasValidationErrorTopTarget) {
      renderErrors(this.validationErrorTopTarget);
    }
    if (this.hasValidationErrorTarget) {
      renderErrors(this.validationErrorTarget);
    }

    // Highlight fields with errors and open their parent accordions
    let firstErrorEl = null;
    const openedAccordions = new Set();
    for (const error of errors) {
      if (typeof error === "object" && error.field) {
        error.field.classList.add("border-red-500");
        error.field.setAttribute("data-validation-error", "true");
        if (!firstErrorEl) firstErrorEl = error.field;

        // Open the closest accordion containing this error field
        const accordionContent = error.field.closest("[data-accordion-id]");
        const accordionId = accordionContent?.getAttribute("data-accordion-id");
        if (accordionId && !openedAccordions.has(accordionId)) {
          openedAccordions.add(accordionId);
          const toggler = document.querySelector(`[href="#${accordionId}"]`);
          if (toggler && !toggler.classList.contains("st-accordion__icon--opened")) {
            const accordion = toggler.closest("[data-controller='ui--accordion']");
            if (accordion) {
              const ctrl = this.application.getControllerForElementAndIdentifier(
                accordion,
                "ui--accordion"
              );
              if (ctrl) ctrl.open(toggler);
            }
          }
        }
      }
    }

    // Scroll to the top error container or first highlighted field
    const scrollTarget = this.hasValidationErrorTopTarget
      ? this.validationErrorTopTarget
      : firstErrorEl;
    if (scrollTarget) {
      scrollTarget.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }

  _findCheckbox(target) {
    if (target.matches?.("input[type=checkbox]")) return target;
    return target.querySelector("input[type=checkbox]");
  }

  _checkboxChecked(target) {
    const cb = this._findCheckbox(target);
    if (cb) return cb.checked;
    // Support switch components: look for the hidden input that holds the
    // switch value, identified by data-checkbox-toggle-target="hiddenValue"
    // to avoid accidentally matching unrelated hidden inputs.
    const hiddenInput =
      target.querySelector("[data-checkbox-toggle-target='hiddenValue']") ||
      target
        .closest("[data-controller='checkbox-toggle']")
        ?.querySelector("[data-checkbox-toggle-target='hiddenValue']");
    if (hiddenInput) return hiddenInput.value === "true";
    return false;
  }

  _updateAllProductPriceVisibility() {
    const rows = this.element.querySelectorAll(
      "[data-admin--checkout-form-products-target='productRow']:not(.hidden)"
    );
    rows.forEach((row) => {
      const fkSelect = row.querySelector(
        "[data-admin--checkout-form-products-target='foreignKeySelect']"
      );
      if (!fkSelect) return;

      const foreignKey = fkSelect.value || fkSelect.dataset.selectedValue;
      if (!foreignKey) return;

      const product = this._findProductByForeignKey(foreignKey);
      if (!product) return;

      const shouldShow = this._shouldShowPrice(product);
      const priceField = row.querySelector(
        "[data-admin--checkout-form-products-target='priceField']"
      );
      if (priceField) {
        priceField.classList.toggle("hidden", !shouldShow);
      }
      const switchPriceField = row.querySelector(
        "[data-admin--checkout-form-products-target='switchPriceField']"
      );
      if (switchPriceField) {
        switchPriceField.classList.toggle("hidden", !shouldShow);
      }
    });
  }

  _updateAllProductTaxRateVisibility(show) {
    const rows = this.element.querySelectorAll(
      "[data-admin--checkout-form-products-target='productRow']:not(.hidden)"
    );
    rows.forEach((row) => {
      const taxRateField = row.querySelector(
        "[data-admin--checkout-form-products-target='taxRateField']"
      );
      if (taxRateField) {
        taxRateField.classList.toggle("hidden", !show);
      }
    });
  }

  _updateIcsContainerVisibility() {
    if (!this.hasIcsOptionsTarget) return;
    const products = this.getAvailableProducts();
    const isIcs = products.some((p) => p.type === "WashConnectProduct");
    this.icsOptionsTarget.classList.toggle("hidden", !isIcs);
  }

  _updateDeliveryUrlVisibility() {
    const meta = this._rgOptionsFrame?.querySelector("[data-location-count]");
    const isDrb = meta?.dataset?.isDrb === "true";
    // Match show_delivery_url_field in Admin::CheckoutFormsController: do not show multi-qty UI
    // for DRB alone — require the Flipper flag and (unless sitewatch_ewash query param) Ewash rows.
    const showDelivery =
      this.sitewatchEwashParamValue ||
      (isDrb && this.multiQtyCheckoutFormEnabledValue && this.ewashRecordsForProductsValue);
    this.showDeliveryUrlValue = showDelivery;

    // toggle delivery URL fields on all product rows
    this.element
      .querySelectorAll("[data-admin--checkout-form-products-target='deliveryUrlField']")
      .forEach((el) => el.classList.toggle("hidden", !showDelivery));

    // toggle DRB alert
    if (this.hasDrbAlertTarget) {
      this.drbAlertTarget.classList.toggle("hidden", !showDelivery);
    }
  }

  _updateIcsOptionsVisibility() {
    if (!this.hasSkipIcsCustomerCreationTarget && !this.hasIcsCorporateSiteTarget) return;

    const rows = this.element.querySelectorAll(
      "[data-admin--checkout-form-products-target='productRow']:not(.hidden)"
    );

    let hasGiftCard = false;
    let hasCorporateSiteEligible = false;

    rows.forEach((row) => {
      const fkSelect = row.querySelector(
        "[data-admin--checkout-form-products-target='foreignKeySelect']"
      );
      const foreignKey = fkSelect?.value || fkSelect?.dataset?.selectedValue;
      if (!foreignKey) return;

      const product = this._findProductByForeignKey(foreignKey);
      const productType = product?.product_type;
      if (productType === "GiftCard") hasGiftCard = true;
      if (productType === "GiftCard" || productType === "Base" || productType === "WashBook")
        hasCorporateSiteEligible = true;
    });

    if (this.hasSkipIcsCustomerCreationTarget) {
      this.skipIcsCustomerCreationTarget.classList.toggle("hidden", !hasGiftCard);
    }
    if (this.hasIcsCorporateSiteTarget) {
      this.icsCorporateSiteTarget.classList.toggle("hidden", !hasCorporateSiteEligible);
    }
  }
}
